import { getReferenceString } from "@medplum/core";
import {
  DATA_ABSENT_REASON_SYSTEM,
  DIAGNOSTIC_CHOICE_CODE_SYSTEM,
  DIAGNOSTIC_DEFINITION_CODE_SYSTEM,
  DIAGNOSTIC_EXECUTION_DECLARATION_SNAPSHOT_URL,
  DIAGNOSTIC_OBSERVATION_COMPONENT_CODE_SYSTEM,
  DIAGNOSTIC_OBSERVATION_DEFINITION_CODE_SYSTEM,
  DIAGNOSTIC_OBSERVATION_ID_SYSTEM,
  DIAGNOSTIC_OBSERVATION_KIND,
  DIAGNOSTIC_ORDER_SET_ID_URL,
  DIAGNOSTIC_PROCEDURE_ENTITY_ID_SYSTEM,
  DIAGNOSTIC_PROCEDURE_KIND,
  DIAGNOSTIC_TRIAL_KIND,
  DIAGNOSTIC_TRIAL_SOURCE_URL,
  DIAGNOSTIC_UNCAPTURED_ID_SYSTEM,
  DIAGNOSTIC_UNCAPTURED_KIND,
  diagnosticResourceKindTag,
  memberKey,
  ORDER_ID_URL,
  syncKey,
  unitToQuantity,
} from "@repo/api";
import type {
  AdjustDiagnosticProcedureTimesInput,
  ClearDiagnosticObservationUnableToCaptureInput,
  CorrectDiagnosticObservationInput,
  MarkDiagnosticObservationUnableToCaptureInput,
  RecordDiagnosticObservationInput,
  RecordDiagnosticObservationsInput,
  RecordDiagnosticProcedureOutcomeInput,
  ReopenDiagnosticProcedureInput,
  RetractDiagnosticObservationInput,
  RetractDiagnosticProcedureDidNotPerformInput,
  SetDiagnosticProcedureNotesInput,
} from "@repo/api/schemas";
import {
  clearDiagnosticObservationUnableToCaptureInputSchema,
  correctDiagnosticObservationInputSchema,
  type ImportDiagnosticDocumentInput,
  importDiagnosticDocumentInputSchema,
  markDiagnosticObservationUnableToCaptureInputSchema,
  recordDiagnosticObservationInputSchema,
  recordDiagnosticObservationsInputSchema,
  recordDiagnosticProcedureOutcomeInputSchema,
  reopenDiagnosticProcedureInputSchema,
  retractDiagnosticObservationInputSchema,
  retractDiagnosticProcedureDidNotPerformInputSchema,
  setDiagnosticProcedureNotesInputSchema,
} from "@repo/api/schemas";
import { DIAGNOSTIC_TRIAL_ID_SYSTEM } from "@repo/fhir-systems/identifiers";
import type { Collection } from "@tanstack/db";
import { defineWrite, type WriteOptions } from "../actions/define-write";
import { orderJoinKeys } from "../fhir/collections";
import {
  type PinnedDiagnosticDeclaration,
  readPinnedDiagnosticDeclaration,
} from "../fhir/diagnostic-procedure-storage";
import { adjustProcedurePeriod } from "../fhir/procedure-period";
import type { ObservationRow, ProcedureRow, RequestGroupRow, ServiceRequestRow } from "../fhir/types";
import {
  beginProcedure,
  type ProcedureIdentity,
  procedureFor,
  procedureReference,
  type TrialRowKind,
  trialRowFor,
  updateProcedureOrder,
  writeTrialRow,
} from "./_procedure-optimistic";
import type { ProcedureTrialKind } from "./_procedure-trial-actions";

type RecordedValue = RecordDiagnosticObservationInput["values"][number]["value"];

type OptimisticCollection<T extends RequestGroupRow | ServiceRequestRow | ProcedureRow | ObservationRow> = Pick<
  Collection<T, string>,
  "state" | "insert" | "update"
>;

export interface DiagnosticActionDependencies {
  importDocument: (input: ImportDiagnosticDocumentInput, options: WriteOptions) => Promise<unknown>;
  requestGroups: () => OptimisticCollection<RequestGroupRow>;
  serviceRequests: () => OptimisticCollection<ServiceRequestRow>;
  procedures: () => OptimisticCollection<ProcedureRow>;
  observations: () => OptimisticCollection<ObservationRow>;
  declarationFor: (serviceRequest: ServiceRequestRow) => PinnedDiagnosticDeclaration;
  adjustTimes: (input: AdjustDiagnosticProcedureTimesInput, options: WriteOptions) => Promise<unknown>;
  recordOutcome: (input: RecordDiagnosticProcedureOutcomeInput, options: WriteOptions) => Promise<unknown>;
  reopen: (input: ReopenDiagnosticProcedureInput, options: WriteOptions) => Promise<unknown>;
  retractDidNotPerform: (
    input: RetractDiagnosticProcedureDidNotPerformInput,
    options: WriteOptions,
  ) => Promise<unknown>;
  recordObservations: (input: RecordDiagnosticObservationsInput, options: WriteOptions) => Promise<unknown>;
  recordObservation: (input: RecordDiagnosticObservationInput, options: WriteOptions) => Promise<unknown>;
  correctObservation: (input: CorrectDiagnosticObservationInput, options: WriteOptions) => Promise<unknown>;
  retractObservation: (input: RetractDiagnosticObservationInput, options: WriteOptions) => Promise<unknown>;
  markObservationUnableToCapture: (
    input: MarkDiagnosticObservationUnableToCaptureInput,
    options: WriteOptions,
  ) => Promise<unknown>;
  clearObservationUnableToCapture: (
    input: ClearDiagnosticObservationUnableToCaptureInput,
    options: WriteOptions,
  ) => Promise<unknown>;
  setNotes: (input: SetDiagnosticProcedureNotesInput, options: WriteOptions) => Promise<unknown>;
}

function orderIn(deps: DiagnosticActionDependencies, orderSetId: string, orderId: string): ServiceRequestRow {
  const order = deps.serviceRequests().state.get(memberKey(orderSetId, orderId));
  if (!order) throw new Error(`Diagnostic order '${orderId}' is not loaded`);
  return order;
}

function valueToFhir(value: RecordedValue) {
  switch (value.kind) {
    case "Quantity":
      return { valueQuantity: { value: value.value, ...unitToQuantity(value.unit) } };
    case "Integer":
      return { valueInteger: value.value };
    case "Boolean":
      return { valueBoolean: value.value };
    case "Choice":
      return {
        valueCodeableConcept: {
          coding: value.selected.map((code) => ({ system: DIAGNOSTIC_CHOICE_CODE_SYSTEM, code })),
          ...(value.freeText ? { text: value.freeText } : {}),
        },
      };
    case "Text":
      return { valueString: value.value };
  }
}

const OWNED_VALUE_KEYS = [
  "valueString",
  "valueQuantity",
  "valueInteger",
  "valueBoolean",
  "valueCodeableConcept",
  "component",
] as const;

function observationFields(declaration: PinnedDiagnosticDeclaration, input: RecordDiagnosticObservationInput) {
  const definition = declaration.observations.find((candidate) => candidate.id === input.definitionId);
  if (!definition) throw new Error(`Diagnostic observation declaration '${input.definitionId}' is not loaded`);
  const single = definition.components.length === 1 && input.values.length === 1 ? input.values[0] : undefined;
  const values = single
    ? valueToFhir(single.value)
    : {
        component: input.values.map((recorded) => ({
          code: {
            coding: [
              {
                system: DIAGNOSTIC_OBSERVATION_COMPONENT_CODE_SYSTEM,
                code: recorded.componentId,
                display: definition.components.find((candidate) => candidate.id === recorded.componentId)?.label,
              },
            ],
          },
          ...valueToFhir(recorded.value),
        })),
      };
  return {
    status: "final" as const,
    effectiveDateTime: input.observedAt,
    note: input.note ? [{ text: input.note }] : undefined,
    code: {
      coding: [
        { system: DIAGNOSTIC_OBSERVATION_DEFINITION_CODE_SYSTEM, code: input.definitionId, display: definition.label },
        ...(single ? [{ system: DIAGNOSTIC_OBSERVATION_COMPONENT_CODE_SYSTEM, code: single.componentId }] : []),
      ],
    },
    ...values,
  };
}

const DIAGNOSTIC_TRIAL_ROW: TrialRowKind = {
  idSystem: DIAGNOSTIC_TRIAL_ID_SYSTEM,
  procedureSystem: DIAGNOSTIC_PROCEDURE_ENTITY_ID_SYSTEM,
  sourceUrl: DIAGNOSTIC_TRIAL_SOURCE_URL,
  tag: diagnosticResourceKindTag(DIAGNOSTIC_TRIAL_KIND),
};

/** `procedureId` is the caller-named identity the server begins the procedure under, when it begins one. */
type ProcedureBeginIdentity = ProcedureIdentity & { procedureId?: string };

function diagnosticProcedure(deps: DiagnosticActionDependencies, input: ProcedureIdentity) {
  return procedureFor(deps.procedures(), input);
}

function newDiagnosticProcedure(
  deps: DiagnosticActionDependencies,
  input: ProcedureBeginIdentity,
  status: ProcedureRow["status"],
): ProcedureRow {
  if (!deps.requestGroups().state.has(input.orderSetId))
    throw new Error(`OrderSet '${input.orderSetId}' is not loaded`);
  const order = orderIn(deps, input.orderSetId, input.orderId);
  const declaration = deps.declarationFor(order);
  const domainId = input.procedureId;
  if (!domainId) throw new Error("A new procedure requires its caller-named identity");
  return {
    resourceType: "Procedure",
    domainId,
    identifier: [{ system: DIAGNOSTIC_PROCEDURE_ENTITY_ID_SYSTEM, value: domainId }],
    meta: { tag: [diagnosticResourceKindTag(DIAGNOSTIC_PROCEDURE_KIND)] },
    instantiatesCanonical: [`${declaration.definitionUrl}|${declaration.version}`],
    basedOn: [
      {
        ...(order.id ? { reference: getReferenceString(order) } : {}),
        identifier: { system: ORDER_ID_URL, value: input.orderId },
      },
    ],
    orderId: input.orderId,
    ...orderJoinKeys(domainId, input.orderSetId, input.orderId),
    status,
    code: {
      coding: [
        { system: DIAGNOSTIC_DEFINITION_CODE_SYSTEM, code: declaration.definitionUrl, display: declaration.name },
      ],
    },
    subject: order.subject as ProcedureRow["subject"],
    ...(order.encounter ? { encounter: order.encounter } : {}),
    extension: [
      { url: DIAGNOSTIC_ORDER_SET_ID_URL, valueString: input.orderSetId },
      { url: DIAGNOSTIC_EXECUTION_DECLARATION_SNAPSHOT_URL, valueString: JSON.stringify(declaration) },
    ],
  };
}

function beginDiagnosticProcedure(deps: DiagnosticActionDependencies, input: ProcedureBeginIdentity, resume = true) {
  return beginProcedure(deps.procedures(), input, () => newDiagnosticProcedure(deps, input, "in-progress"), resume);
}

export function createOrderSetDiagnosticActions(deps: DiagnosticActionDependencies) {
  const recordOutcome = defineWrite<RecordDiagnosticProcedureOutcomeInput>({
    optimistic: (params) => {
      const input = recordDiagnosticProcedureOutcomeInputSchema.parse(params);
      const existing = diagnosticProcedure(deps, input);
      const status =
        input.outcome === "didNotPerform" ? "not-done" : input.outcome === "unableToComplete" ? "stopped" : "completed";
      if (existing) {
        deps.procedures().update(syncKey(existing), (draft) => {
          draft.status = status;
          if (status === "not-done") draft.performedPeriod = undefined;
          if (status === "not-done") draft.performer = undefined;
          if (input.notes !== undefined) draft.note = input.notes ? [{ text: input.notes }] : undefined;
        });
      } else {
        deps.procedures().insert({
          ...newDiagnosticProcedure(deps, input, status),
          ...(input.notes ? { note: [{ text: input.notes }] } : {}),
        });
      }
      updateProcedureOrder(deps, input, input.outcome === "didNotPerform" ? "revoked" : "completed");
    },
    persist: (params, options) => deps.recordOutcome(params, options),
  });

  function writeObservation(input: RecordDiagnosticObservationInput, correction: boolean) {
    const procedure = correction ? diagnosticProcedure(deps, input) : beginDiagnosticProcedure(deps, input);
    if (!procedure) throw new Error("Diagnostic procedure is not loaded");
    const declaration = readPinnedDiagnosticDeclaration(procedure);
    const fields = observationFields(declaration, input);
    const key = memberKey(procedure.domainId, input.observationId);
    const existing = deps.observations().state.get(key);
    if (correction && !existing) {
      throw new Error(`Diagnostic Observation '${input.observationId}' is not loaded`);
    }
    if (existing) {
      deps.observations().update(key, (draft) => {
        draft.status = correction ? "corrected" : fields.status;
        draft.effectiveDateTime = fields.effectiveDateTime;
        draft.note = fields.note;
        draft.code = fields.code;
        for (const key of OWNED_VALUE_KEYS) draft[key] = undefined;
        Object.assign(
          draft,
          Object.fromEntries(
            OWNED_VALUE_KEYS.flatMap((key) => (key in fields ? [[key, fields[key as keyof typeof fields]]] : [])),
          ),
        );
      });
      return;
    }
    deps.observations().insert({
      resourceType: "Observation",
      meta: { tag: [diagnosticResourceKindTag(DIAGNOSTIC_OBSERVATION_KIND)] },
      identifier: [{ system: DIAGNOSTIC_OBSERVATION_ID_SYSTEM, value: input.observationId }],
      ...fields,
      ...(input.trialId
        ? {
            derivedFrom: [
              {
                type: "Observation" as const,
                identifier: { system: DIAGNOSTIC_TRIAL_ID_SYSTEM, value: input.trialId },
              },
            ],
          }
        : {}),
      subject: procedure.subject,
      ...(procedure.encounter ? { encounter: procedure.encounter } : {}),
      partOf: [procedureReference(procedure, DIAGNOSTIC_PROCEDURE_ENTITY_ID_SYSTEM)],
      procedureId: procedure.domainId,
      procedureJoinKey: procedure.domainId,
      // Charted against the Procedure, not against an order: the sentinels
      // keep it out of every order's observation join rather than in all of them.
      ...orderJoinKeys(key, undefined, undefined),
    });
  }

  function observationAction<T extends RecordDiagnosticObservationInput>(
    schema: { parse(input: T): T },
    mutate: (input: T, options: WriteOptions) => Promise<unknown>,
    correction: boolean,
  ) {
    return defineWrite<T>({
      optimistic: (params) => {
        const input = schema.parse(params);
        writeObservation(input, correction);
      },
      persist: (params, options) => mutate(params, options),
    });
  }

  const recordObservation = observationAction(recordDiagnosticObservationInputSchema, deps.recordObservation, false);
  const correctObservation = observationAction(correctDiagnosticObservationInputSchema, deps.correctObservation, true);
  const setNotes = defineWrite<SetDiagnosticProcedureNotesInput>({
    optimistic: (params) => {
      const input = setDiagnosticProcedureNotesInputSchema.parse(params);
      const procedure = beginDiagnosticProcedure(deps, input, false);
      deps.procedures().update(syncKey(procedure), (draft) => {
        draft.note = input.notes ? [{ text: input.notes }] : undefined;
      });
    },
    persist: (params, options) => deps.setNotes(params, options),
  });
  const reopen = defineWrite<ReopenDiagnosticProcedureInput>({
    optimistic: (params) => {
      const input = reopenDiagnosticProcedureInputSchema.parse(params);
      updateProcedureOrder(deps, input, "draft");
      const existing = diagnosticProcedure(deps, input);
      if (!existing) throw new Error(`DiagnosticProcedure for order '${input.orderId}' is not loaded`);
      deps.procedures().update(syncKey(existing), (draft) => {
        // Only the end goes — a resumed procedure does not have one.
        draft.performedPeriod = draft.performedPeriod?.start ? { start: draft.performedPeriod.start } : undefined;
        draft.status = "in-progress";
      });
    },
    persist: (input, options) => deps.reopen(input, options),
  });
  const retractDidNotPerform = defineWrite<RetractDiagnosticProcedureDidNotPerformInput>({
    optimistic: (params) => {
      const input = retractDiagnosticProcedureDidNotPerformInputSchema.parse(params);
      updateProcedureOrder(deps, input, "draft");
      const existing = diagnosticProcedure(deps, input);
      if (!existing) throw new Error(`DiagnosticProcedure for order '${input.orderId}' is not loaded`);
      deps.procedures().update(syncKey(existing), (draft) => {
        draft.status = "entered-in-error";
        draft.performedPeriod = undefined;
      });
    },
    persist: (input, options) => deps.retractDidNotPerform(input, options),
  });
  const importDocument = defineWrite<ImportDiagnosticDocumentInput>({
    optimistic: (params) => {
      const input = importDiagnosticDocumentInputSchema.parse(params);
      const procedure = beginDiagnosticProcedure(deps, input);
      for (const trial of input.trialCorrections ?? []) {
        if (!trialRowFor(deps.observations(), procedure, DIAGNOSTIC_TRIAL_ROW, trial.id))
          throw new Error(`Trial '${trial.id}' is not loaded`);
        writeTrialRow(deps.observations(), procedure, DIAGNOSTIC_TRIAL_ROW, trial, true);
      }
      for (const trial of input.trials)
        writeTrialRow(deps.observations(), procedure, DIAGNOSTIC_TRIAL_ROW, trial, false);
      for (const observation of input.observationCorrections ?? [])
        writeObservation({ ...input, ...observation }, true);
      for (const observation of input.observations ?? []) writeObservation({ ...input, ...observation }, false);
    },
    persist: (input, options) => deps.importDocument(input, options),
  });
  const recordObservations = defineWrite<RecordDiagnosticObservationsInput>({
    optimistic: (params) => {
      const input = recordDiagnosticObservationsInputSchema.parse(params);
      for (const observation of input.observations) writeObservation({ ...input, ...observation }, false);
    },
    persist: (input, options) => deps.recordObservations(input, options),
  });
  const retractObservation = defineWrite<RetractDiagnosticObservationInput>({
    optimistic: (params) => {
      const input = retractDiagnosticObservationInputSchema.parse(params);
      const procedure = diagnosticProcedure(deps, input);
      if (!procedure) throw new Error("Diagnostic procedure is not loaded");
      deps.observations().update(memberKey(procedure.domainId, input.observationId), (draft) => {
        draft.status = "entered-in-error";
      });
    },
    persist: (input, options) => deps.retractObservation(input, options),
  });
  const markObservationUnableToCapture = defineWrite<MarkDiagnosticObservationUnableToCaptureInput>({
    optimistic: (params) => {
      const input = markDiagnosticObservationUnableToCaptureInputSchema.parse(params);
      const procedure = beginDiagnosticProcedure(deps, input);
      const key = memberKey(procedure.domainId, input.definitionId);
      const dataAbsentReason = { coding: [{ system: DATA_ABSENT_REASON_SYSTEM, code: "error" }] };
      if (deps.observations().state.has(key)) {
        deps.observations().update(key, (draft) => {
          draft.status = "final";
          draft.dataAbsentReason = dataAbsentReason;
        });
      } else {
        deps.observations().insert({
          resourceType: "Observation",
          identifier: [{ system: DIAGNOSTIC_UNCAPTURED_ID_SYSTEM, value: input.definitionId }],
          meta: { tag: [diagnosticResourceKindTag(DIAGNOSTIC_UNCAPTURED_KIND)] },
          status: "final",
          code: { coding: [{ system: DIAGNOSTIC_OBSERVATION_DEFINITION_CODE_SYSTEM, code: input.definitionId }] },
          subject: procedure.subject,
          encounter: procedure.encounter,
          partOf: [procedureReference(procedure, DIAGNOSTIC_PROCEDURE_ENTITY_ID_SYSTEM)],
          procedureId: procedure.domainId,
          procedureJoinKey: procedure.domainId,
          ...orderJoinKeys(key, undefined, undefined),
          dataAbsentReason,
        });
      }
    },
    persist: (input, options) => deps.markObservationUnableToCapture(input, options),
  });
  const clearObservationUnableToCapture = defineWrite<ClearDiagnosticObservationUnableToCaptureInput>({
    optimistic: (params) => {
      const input = clearDiagnosticObservationUnableToCaptureInputSchema.parse(params);
      const procedure = diagnosticProcedure(deps, input);
      if (!procedure) throw new Error("Diagnostic procedure is not loaded");
      deps.observations().update(memberKey(procedure.domainId, input.definitionId), (draft) => {
        draft.status = "entered-in-error";
      });
    },
    persist: (input, options) => deps.clearObservationUnableToCapture(input, options),
  });
  return {
    adjustTimes: defineWrite<AdjustDiagnosticProcedureTimesInput>({
      optimistic: (input) => {
        const row = diagnosticProcedure(deps, input);
        if (!row) throw new Error("Diagnostic procedure is not loaded");
        deps.procedures().update(syncKey(row), (draft) => {
          draft.performedPeriod = adjustProcedurePeriod(draft.performedPeriod, input);
        });
      },
      persist: (input, options) => deps.adjustTimes(input, options),
    }),
    trialKind: {
      row: DIAGNOSTIC_TRIAL_ROW,
      begin: (input) => beginDiagnosticProcedure(deps, input),
    } satisfies ProcedureTrialKind,
    importDocument,
    recordObservations,
    recordObservation,
    correctObservation,
    retractObservation,
    markObservationUnableToCapture,
    clearObservationUnableToCapture,
    recordOutcome,
    reopen,
    retractDidNotPerform,
    setNotes,
  };
}
